<template>
  <v-card class="pa-5" elevation="0">
    <template v-if="isPdf && !isTooLarge">
      <!-- Cartera PDF (PDF Portfolio): pdf.js no puede representarla. Sus
           documentos se añaden al selector de ficheros del detalle. -->
      <div v-show="isPortfolio">
        <p
          class="styled-text"
          v-html="
            $t(
              hasEmbeddedFiles
                ? 'pdfPortfolioViewerNotice'
                : 'nonPdfViewerNotice'
            )
          "
        ></p>
      </div>

      <!-- Visor de PDF estándar. Se mantiene montado con v-show (en lugar de
           v-if) para no destruir el worker compartido de pdf.js al mostrar una
           cartera. -->
      <vue-pdf-embed
        v-show="!isPortfolio"
        :source="pdfsrc"
        @loaded="onPdfLoaded"
      />
    </template>

    <!-- PDF demasiado grande -->
    <div v-else-if="isPdf && isTooLarge">
      <p
        v-html="
          $t('pdfTooLargeViewerNotice', {
            sizeMB: sizeMB,
            PDF_MAX_SIZE_MB: PDF_MAX_SIZE_MB,
          })
        "
        class="styled-text"
      ></p>
    </div>

    <!-- Archivos RTF -->
    <div v-else-if="isRtf" v-html="rtfHtml"></div>

    <!-- Otros tipos de archivos no soportados -->
    <div v-else>
      <p v-html="$t('nonPdfViewerNotice')" class="styled-text"></p>
    </div>
  </v-card>
</template>
<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import { EMFJS, RTFJS, WMFJS } from 'rtf.js'

const mime = require('mime-types')

export default {
  components: {
    VuePdfEmbed,
  },
  props: {
    blob: Blob,
    contentType: {
      type: String,
      required: true,
    },
    rtfString: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      rtfHtml: '',
      PDF_MAX_SIZE_MB: 8, // tamaño máximo en MB del archivo PDF
      isPortfolio: false,
      hasEmbeddedFiles: false,
      portfolioExtracted: false,
    }
  },
  computed: {
    pdfsrc() {
      // Solo generamos URL si no es demasiado grande
      return !this.isTooLarge ? URL.createObjectURL(this.blob) : null
    },
    isPdf() {
      return this.contentType === 'application/pdf'
    },
    isRtf() {
      return (
        this.contentType === 'text/richtext' ||
        this.contentType === 'application/rtf'
      )
    },
    sizeMB() {
      return (this.blob.size / (1024 * 1024)).toFixed(2)
    },
    isTooLarge() {
      return this.isPdf && this.sizeMB > this.PDF_MAX_SIZE_MB
    },
  },
  methods: {
    resetPortfolio() {
      this.isPortfolio = false
      this.hasEmbeddedFiles = false
      this.portfolioExtracted = false
    },
    /**
     * Se ejecuta cuando el PDF se ha cargado. Si es una cartera PDF (PDF
     * Portfolio) pdf.js únicamente renderiza su portada informativa, por lo que
     * extraemos los ficheros embebidos y los notificamos al componente padre
     * para que los añada al selector de ficheros del detalle.
     */
    async onPdfLoaded(pdf) {
      if (this.portfolioExtracted) return

      try {
        if (!(await this.isPdfPortfolio(pdf))) return

        this.portfolioExtracted = true

        const attachments = await pdf.getAttachments()
        const files = []
        for (const [key, attachment] of Object.entries(attachments || {})) {
          const filename = attachment.filename || key
          const content = attachment.content
          const bytes =
            content instanceof Uint8Array
              ? content.slice()
              : new Uint8Array(content)
          const contentType =
            mime.lookup(filename) || 'application/octet-stream'

          files.push({
            name: filename,
            contentType,
            blob: new Blob([bytes], { type: contentType }),
          })
        }

        this.hasEmbeddedFiles = files.length > 0
        if (files.length) this.$emit('embedded-files', files)
        this.isPortfolio = true
      } catch (error) {
        // Si no se puede comprobar, dejamos el visor de PDF estándar
        //console.log('No se ha podido verificar si el PDF es una cartera', error)
      }
    },
    /**
     * Detecta si el PDF es una cartera PDF (PDF Portfolio). pdf.js expone esta
     * información en los metadatos (IsCollectionPresent). Como respaldo, si no
     * está disponible, se comprueba el texto de la portada que Adobe inserta.
     */
    async isPdfPortfolio(pdf) {
      const metadata = await pdf.getMetadata()
      if (metadata?.info?.IsCollectionPresent) return true
      return this.hasPortfolioCoverText(pdf)
    },
    /**
     * Detecta la portada informativa que Adobe inserta en las carteras PDF y
     * que pdf.js no puede representar.
     */
    async hasPortfolioCoverText(pdf) {
      if (!pdf?.numPages) return false

      const page = await pdf.getPage(1)
      const textContent = await page.getTextContent()
      const text = textContent.items
        .map((item) => item.str || '')
        .join(' ')
        .toLowerCase()

      return (
        text.includes('cartera pdf') ||
        text.includes('pdf portfolio') ||
        text.includes('open this pdf portfolio') ||
        text.includes('abra esta cartera')
      )
    },
    async processRtf() {
      if (!this.isRtf || !this.rtfString) return ''
      try {
        RTFJS.loggingEnabled(false)
        WMFJS.loggingEnabled(false)
        EMFJS.loggingEnabled(false)

        const doc = new RTFJS.Document(this.stringToArrayBuffer(this.rtfString))
        // Por el momento creo que los metadatos no nos interesan
        //const meta = doc.metadata()
        //console.log('Meta: ', meta)

        // Renderizar el contenido
        const htmlElements = await doc.render()

        // Combinar todos los divs del array en un solo string HTML
        const combinedHtml = htmlElements.map((div) => div.outerHTML).join('')

        // Asignar a la variable que se carga en v-html
        this.rtfHtml = combinedHtml
      } catch (error) {
        //console.log('Error procesando el archivo RTF: ', error)
        this.rtfHtml = <p>this.$t('nonPdfViewerNotice')</p>
      }
    },
    stringToArrayBuffer(string) {
      const buffer = new ArrayBuffer(string.length)
      const bufferView = new Uint8Array(buffer)
      for (let i = 0; i < string.length; i++) {
        bufferView[i] = string.charCodeAt(i)
      }
      return buffer
    },
  },
  watch: {
    rtfString: 'processRtf',
    blob() {
      this.resetPortfolio()
    },
  },
}
</script>
<style lang="scss" scoped>
.styled-text {
  font-weight: bold;
  /*font-size: 1.1em;*/ /* Tamaño de fuente mas grande */
}
</style>
